// ============================================================
// BOOTH — the assistant's three sounds.
//
// Synthesised, not sampled. Three cues this short are a few oscillators and
// an envelope each, which costs nothing to ship, needs no licence, works
// offline, and can be tuned by changing a number instead of re-cutting a
// file.
//
// The brief was Jarvis: clean, quiet, slightly electronic. That means sine
// and triangle waves rather than square, a lowpass with a little resonance
// so each cue has a throat to it, a second voice detuned a few cents for
// width, and envelopes measured in tens of milliseconds. Nothing here is
// meant to be noticed on its own — a person should feel the interface
// answering, not hear a notification.
//
// Rules that matter more than the timbre:
//
//  - The AudioContext is created on the first gesture and never before.
//    Browsers refuse one made at load, and a page that tries is a page that
//    logs a warning on every visit.
//  - Everything is wrapped. Audio is a nicety; a browser that refuses it
//    must not take a click handler down with it.
//  - It stays quiet. Peak gain is 0.05, and the cues sit under 200ms.
// ============================================================

let ctx: AudioContext | null = null

/** The context, made on demand. Null means this browser will not give us one,
 *  which is a silent interface and nothing worse. */
function audio(): AudioContext | null {
  if (typeof window === "undefined") return null
  try {
    if (!ctx) {
      const Ctor = window.AudioContext ?? (window as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext
      if (!Ctor) return null
      ctx = new Ctor()
    }
    // Autoplay policy parks a context created before the first gesture.
    if (ctx.state === "suspended") void ctx.resume()
    return ctx
  } catch {
    return null
  }
}

type Voice = {
  /** Hz at the start. */
  from: number
  /** Hz at the end, if it should move. */
  to?: number
  type?: OscillatorType
  /** Seconds from the cue's start. */
  at?: number
  dur?: number
  gain?: number
  /** Cents, for the second voice of a pair. */
  detune?: number
}

function play(voices: Voice[]) {
  const ac = audio()
  if (!ac) return
  try {
    const now = ac.currentTime
    // One filter for the whole cue: it is what makes these read as one
    // instrument rather than a handful of beeps.
    const tone = ac.createBiquadFilter()
    tone.type = "lowpass"
    tone.frequency.setValueAtTime(2600, now)
    tone.Q.value = 0.9
    tone.connect(ac.destination)

    for (const v of voices) {
      const start = now + (v.at ?? 0)
      const dur = v.dur ?? 0.12
      const peak = v.gain ?? 0.05

      const osc = ac.createOscillator()
      osc.type = v.type ?? "sine"
      osc.frequency.setValueAtTime(v.from, start)
      if (v.to) osc.frequency.exponentialRampToValueAtTime(v.to, start + dur)
      if (v.detune) osc.detune.setValueAtTime(v.detune, start)

      // Fast in, slow out. A linear fade out clicks; an exponential one to a
      // floor above zero does not.
      const env = ac.createGain()
      env.gain.setValueAtTime(0.0001, start)
      env.gain.exponentialRampToValueAtTime(peak, start + 0.012)
      env.gain.exponentialRampToValueAtTime(0.0001, start + dur)

      osc.connect(env)
      env.connect(tone)
      osc.start(start)
      osc.stop(start + dur + 0.02)
    }
  } catch {
    // Silent, deliberately. Nothing about a UI sound is worth an exception.
  }
}

export const chatSound = {
  /** Opening the panel: two notes a fifth apart, rising, like something
   *  coming online. */
  open() {
    play([
      { from: 392, to: 587, dur: 0.17, gain: 0.045 },
      { from: 784, to: 1175, dur: 0.17, gain: 0.018, type: "triangle", at: 0.015 },
    ])
  },
  /** Closing: the same shape, downward and shorter. */
  close() {
    play([{ from: 587, to: 330, dur: 0.13, gain: 0.035 }])
  },
  /** Sending: one short blip up. Quiet — it fires on every message. */
  send() {
    play([
      { from: 660, to: 990, dur: 0.075, gain: 0.035 },
      { from: 660, to: 990, dur: 0.075, gain: 0.012, detune: 7, type: "triangle" },
    ])
  },
  /** An answer arriving: two notes settling, the second a third above and a
   *  beat later, so it reads as a reply rather than an alert. */
  receive() {
    play([
      { from: 880, dur: 0.1, gain: 0.03 },
      { from: 1174, dur: 0.14, gain: 0.026, at: 0.085 },
      { from: 2349, dur: 0.1, gain: 0.008, at: 0.085, type: "triangle" },
    ])
  },
}
